import React, { useEffect, useMemo, useRef, useState } from "react";
import { Label, Spinner } from "reactstrap";
import {
	isWritingRecommendationSelectable,
	recommendationIdsMatch,
} from "./writingAssistantUtils";

const AI_WRITING_ASSISTANT_LABEL = "Use AI Writing Assistant";

function recommendationMeta(rec) {
	const parts = [];
	const owner = rec.suggested_owner?.trim();
	const severity = rec.severity?.trim();
	if (owner) parts.push(`Owner: ${owner}`);
	if (severity) parts.push(severity);
	return parts.length ? parts.join(" · ") : null;
}

function RecommendationCardContent({ rec, showAiBadge = false, showManualBadge = false }) {
	const meta = recommendationMeta(rec);

	return (
		<div className="flex-grow-1 min-w-0">
			<div className="writing-assistance__rec-card-head">
				<strong className="writing-assistance__rec-title">{rec.title}</strong>
				<div className="writing-assistance__rec-badges">
					{showAiBadge && (
						<span className="writing-assistance__ai-fix-badge">
							<i className="fas fa-wand-magic-sparkles me-1" />
							Writing Assistant
						</span>
					)}
					{showManualBadge && (
						<span className="writing-assistance__unavailable-badge">
							<i className="fas fa-user-pen me-1" />
							Manual
						</span>
					)}
					{rec.processed && (
						<span className="writing-assistance__processed-badge">
							<i className="fas fa-check me-1" />
							Processed
						</span>
					)}
					{!rec.processed && rec.partially_processed && (
						<span className="writing-assistance__partial-badge">
							<i className="fas fa-circle-half-stroke me-1" />
							Partially processed
						</span>
					)}
				</div>
			</div>
			<p className="writing-assistance__rec-desc mb-2">{rec.description}</p>
			{meta ? (
				<p className="writing-assistance__rec-meta mb-0">{meta}</p>
			) : null}
		</div>
	);
}

export default function RecommendationSection({
	auditId,
	recommendations,
	loading,
	selectedRecommendationId,
	onSelect,
}) {
	const scrollRef = useRef(null);
	const scrolledForAuditRef = useRef(null);
	const [activeTab, setActiveTab] = useState("ai");

	const { aiFixable, manualOnly } = useMemo(() => {
		const list = Array.isArray(recommendations) ? recommendations : [];
		return {
			aiFixable: list.filter(isWritingRecommendationSelectable),
			manualOnly: list.filter((rec) => !isWritingRecommendationSelectable(rec)),
		};
	}, [recommendations]);

	const visibleRecs = activeTab === "ai" ? aiFixable : manualOnly;

	useEffect(() => {
		scrolledForAuditRef.current = null;
		setActiveTab("ai");
	}, [auditId]);

	useEffect(() => {
		if (
			loading ||
			auditId == null ||
			selectedRecommendationId == null ||
			activeTab !== "ai" ||
			!aiFixable.length
		) {
			return;
		}

		const auditKey = String(auditId);
		if (scrolledForAuditRef.current === auditKey) return;
		scrolledForAuditRef.current = auditKey;

		requestAnimationFrame(() => {
			const selected = aiFixable.find((rec) =>
				recommendationIdsMatch(rec.id, selectedRecommendationId)
			);
			if (!selected) return;

			const card = scrollRef.current?.querySelector(
				`[data-rec-id="${selected.id}"]`
			);
			card?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	}, [activeTab, aiFixable, auditId, loading, selectedRecommendationId]);

	if (loading) {
		return (
			<div className="writing-assistance__field text-center py-3">
				<Spinner size="sm" className="me-2" />
				<span className="text-muted small">Loading recommendations…</span>
			</div>
		);
	}

	if (!recommendations) return null;

	return (
		<div className="writing-assistance__field">
			<Label className="writing-assistance__label">Recommendation</Label>
			<p className="writing-assistance__rec-hint mb-2">
				Choose a recommendation under <strong>{AI_WRITING_ASSISTANT_LABEL}</strong> to
				generate a preview. Manual items are handled outside this tool.
			</p>

			{recommendations.length === 0 ? (
				<p className="text-muted small mb-0">No recommendations available for this audit.</p>
			) : (
				<>
					<div
						className="writing-assistance__rec-tabs"
						role="tablist"
						aria-label="Recommendation type"
					>
						<button
							type="button"
							role="tab"
							id="writing-rec-tab-ai"
							aria-selected={activeTab === "ai"}
							aria-controls="writing-rec-panel-ai"
							className={`writing-assistance__rec-tab${
								activeTab === "ai" ? " writing-assistance__rec-tab--active" : ""
							}`}
							onClick={() => setActiveTab("ai")}
						>
							{AI_WRITING_ASSISTANT_LABEL} ({aiFixable.length})
						</button>
						<button
							type="button"
							role="tab"
							id="writing-rec-tab-manual"
							aria-selected={activeTab === "manual"}
							aria-controls="writing-rec-panel-manual"
							className={`writing-assistance__rec-tab${
								activeTab === "manual" ? " writing-assistance__rec-tab--active" : ""
							}`}
							onClick={() => setActiveTab("manual")}
						>
							Manual ({manualOnly.length})
						</button>
					</div>

					<div
						ref={scrollRef}
						className="writing-assistance__rec-scroll"
						role="tabpanel"
						id={activeTab === "ai" ? "writing-rec-panel-ai" : "writing-rec-panel-manual"}
						aria-labelledby={
							activeTab === "ai" ? "writing-rec-tab-ai" : "writing-rec-tab-manual"
						}
					>
						{visibleRecs.length === 0 ? (
							<p className="text-muted small mb-0 px-1 py-2">
								{activeTab === "ai"
									? `No recommendations for ${AI_WRITING_ASSISTANT_LABEL} on this audit.`
									: "No manual recommendations for this audit."}
							</p>
						) : activeTab === "ai" ? (
							visibleRecs.map((rec) => {
								const isSelected = recommendationIdsMatch(
									rec.id,
									selectedRecommendationId
								);

								return (
									<button
										key={rec.id}
										type="button"
										data-rec-id={rec.id}
										className={`writing-assistance__rec-card text-start w-100${
											isSelected ? " writing-assistance__rec-card--selected" : ""
										}`}
										onClick={() => onSelect(rec.id)}
									>
										<div className="d-flex align-items-start gap-3">
											<input
												type="radio"
												name="writing-recommendation"
												checked={isSelected}
												readOnly
												className="writing-assistance__rec-radio mt-1"
												aria-hidden
											/>
											<RecommendationCardContent rec={rec} showAiBadge />
										</div>
									</button>
								);
							})
						) : (
							visibleRecs.map((rec) => (
								<div
									key={rec.id}
									data-rec-id={rec.id}
									className="writing-assistance__rec-card writing-assistance__rec-card--readonly w-100"
								>
									<RecommendationCardContent rec={rec} showManualBadge />
								</div>
							))
						)}
					</div>
				</>
			)}
		</div>
	);
}
